import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Breadcrumbs,
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
} from "@mui/material";
import {
  useParams,
  useNavigate,
  Link as RouterLink,
  useLocation,
} from "react-router-dom";
import { WorkflowSpansTab } from "./WorkflowSpansTab";
import { WorkflowCronsTab } from "./WorkflowCronsTab";
import { useStartWorkflow } from "../../../clients/graphql/pages/workflow-queries";
import UpdateIcon from "@mui/icons-material/Update";
import UpdateDisabledIcon from "@mui/icons-material/UpdateDisabled";
import RefreshIcon from "@mui/icons-material/Refresh";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`workflow-tabpanel-${index}`}
      aria-labelledby={`workflow-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CustomJsonEditor: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [lineNumbers, setLineNumbers] = useState<string>("1");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      const lines = value.split("\n").length;
      setLineNumbers(Array.from({ length: lines }, (_, i) => i + 1).join("\n"));
    } else {
      setLineNumbers("1");
    }
  }, [value]);

  const handleScroll = () => {
    if (textareaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "400px",
        border: "1px solid #ccc",
        borderRadius: "4px",
      }}
    >
      <Box
        ref={lineNumbersRef}
        sx={{
          width: "40px",
          padding: "8px",
          backgroundColor: "#f5f5f5",
          borderRight: "1px solid #ccc",
          overflow: "hidden",
          fontFamily: "monospace",
          fontSize: "14px",
          lineHeight: "20px",
          color: "#666",
          userSelect: "none",
        }}
      >
        {lineNumbers}
      </Box>
      <TextField
        inputRef={textareaRef}
        multiline
        fullWidth
        value={value}
        onChange={handleChange}
        onScroll={handleScroll}
        sx={{
          "& .MuiInputBase-root": {
            height: "100%",
            fontFamily: "monospace",
            fontSize: "14px",
            lineHeight: "20px",
            padding: "8px",
          },
          "& .MuiInputBase-input": {
            height: "100% !important",
            overflow: "auto !important",
          },
        }}
        InputProps={{
          disableUnderline: true,
        }}
      />
    </Box>
  );
};

export const WorkflowDetailPage: React.FC = () => {
  const { workflowId, tabName } = useParams<{
    workflowId: string;
    tabName?: string;
  }>();
  const [tabValue, setTabValue] = useState(tabName === "crons" ? 1 : 0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [jsonInput, setJsonInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [startWorkflow] = useStartWorkflow();
  const location = useLocation();
  const [pollingInterval, setPollingInterval] = useState<number>(0); // Default disabled
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [refreshKey, setRefreshKey] = useState(0); // Used to force re-render of tabs

  useEffect(() => {
    // Update tab value when URL changes
    setTabValue(location.pathname.includes("/crons") ? 1 : 0);
  }, [location.pathname, workflowId]);

  // Setup polling effect
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (pollingInterval > 0) {
      timer = setInterval(() => {
        setRefreshKey((prev) => prev + 1);
      }, pollingInterval);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [pollingInterval]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue !== 2) setTabValue(newValue);

    if (newValue === 0) {
      navigate(`/developer/workflows/${workflowId}`);
    } else if (newValue === 1) {
      navigate(`/developer/workflows/${workflowId}/crons`);
    } else if (newValue === 2) {
      const url = new URL(
        "https://grafana-478860069183.us-central1.run.app/d/eels70fe5tfcwb/workflow-insights"
      );
      const params = new URLSearchParams();
      params.append("var-workflowName", workflowId ?? "");
      params.append("orgId", "1");
      params.append("kiosk", "true");
      url.search = params.toString();

      window.open(url.toString(), "_blank");
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setJsonInput("");
  };

  const handleJsonInputChange = (value: string) => {
    setJsonInput(value);
  };

  const handleRefreshMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRefreshMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRefreshIntervalChange = (interval: number) => {
    setPollingInterval(interval);
    handleRefreshMenuClose();
  };

  const getPollingLabel = (interval: number) => {
    if (interval === 0) return "Disabled";
    if (interval === 1000) return "1s";
    if (interval === 5000) return "5s";
    if (interval === 60000) return "1m";
    return "";
  };

  const handleManualRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleSubmit = async () => {
    if (!workflowId) return;

    try {
      setIsSubmitting(true);
      const { data } = await startWorkflow({
        variables: {
          input: {
            workflowName: workflowId,
            payload: JSON.parse(jsonInput),
          },
        },
      });

      if (data.startWorkflow.success) {
        handleCloseModal();
        navigate(
          `/developer/workflows/${workflowId}/executions/${data.startWorkflow.workflowId}`
        );
      } else {
        console.error("Failed to start workflow");
        // TODO: Add error handling/notification
      }
    } catch (error) {
      console.error("Failed to start workflow:", error);
      // TODO: Add error handling/notification
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
        <Breadcrumbs>
          <Link component={RouterLink} to="/developer" color="inherit">
            Developer
          </Link>
          <Link
            component={RouterLink}
            to="/developer/workflows"
            color="inherit"
          >
            Workflows
          </Link>
          <Typography color="text.primary">{workflowId}</Typography>
          {tabValue === 1 && (
            <Typography color="text.primary">Crons</Typography>
          )}
          {tabValue === 2 && (
            <Typography color="text.primary">Insights</Typography>
          )}
        </Breadcrumbs>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4">Workflow Details</Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Tooltip title="Refresh rate">
              <Button
                onClick={handleRefreshMenuClick}
                size="small"
                variant="outlined"
                startIcon={
                  pollingInterval === 0 ? (
                    <UpdateDisabledIcon />
                  ) : (
                    <UpdateIcon />
                  )
                }
              >
                {getPollingLabel(pollingInterval)}
              </Button>
            </Tooltip>
            <Button
              onClick={handleManualRefresh}
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenModal}
            >
              New Workflow
            </Button>
          </Box>
        </Box>
      </Box>
      <Paper sx={{ width: "100%" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="workflow tabs"
        >
          <Tab
            label="Executions"
            component={RouterLink}
            to={`/developer/workflows/${workflowId}`}
          />
          <Tab
            label="Crons"
            component={RouterLink}
            to={`/developer/workflows/${workflowId}/crons`}
          />
          <Tab label="Insights" component={RouterLink} to={location.pathname} />
        </Tabs>
        <TabPanel value={tabValue} index={0}>
          <WorkflowSpansTab
            workflowId={workflowId!}
            spanType="WORKFLOW"
            key={`executions-${refreshKey}`}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <WorkflowCronsTab
            workflowId={workflowId!}
            key={`crons-${refreshKey}`}
          />
        </TabPanel>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleRefreshMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() => handleRefreshIntervalChange(0)}
          selected={pollingInterval === 0}
        >
          <ListItemIcon>
            <UpdateDisabledIcon fontSize="small" />
          </ListItemIcon>
          Disabled
        </MenuItem>
        <MenuItem
          onClick={() => handleRefreshIntervalChange(1000)}
          selected={pollingInterval === 1000}
        >
          <ListItemIcon>
            <UpdateIcon fontSize="small" />
          </ListItemIcon>
          1 second
        </MenuItem>
        <MenuItem
          onClick={() => handleRefreshIntervalChange(5000)}
          selected={pollingInterval === 5000}
        >
          <ListItemIcon>
            <UpdateIcon fontSize="small" />
          </ListItemIcon>
          5 seconds
        </MenuItem>
        <MenuItem
          onClick={() => handleRefreshIntervalChange(60000)}
          selected={pollingInterval === 60000}
        >
          <ListItemIcon>
            <UpdateIcon fontSize="small" />
          </ListItemIcon>
          1 minute
        </MenuItem>
      </Menu>

      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Trigger New Workflow</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <CustomJsonEditor
              value={jsonInput}
              onChange={handleJsonInputChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting || !jsonInput}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
